/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable no-new */
/* eslint-disable new-cap */
/* eslint-disable no-undef */
import { fabric } from 'fabric';
import { DocumentHighlightRectDto } from '@api/Api';
import { hashedColor } from '@helpers/hashedColor';
import {
  bindOverObjectRef,
  markCommentTargetClick,
  type HighlightCreatorViewerActions
} from '@pages/MediaViewer/AssetViewer/Canvas/Objects/highlightHandler';

type PinCreateProps = {
  e?: fabric.IEvent;
  x?: number;
  y?: number;
  canvas: fabric.Canvas;
  overObject: { current: boolean };
  userEmail: string;
  userName: string;
  // URL of the user's picture, shown instead of the initials
  userPicture?: string | null;
  objectToCreate: { current: fabric.Object | undefined };
  scale: number;
};

type PinPointCreatorProps = {
  canvas: fabric.Canvas;
  userEmail: string;
  userName: string;
  // URL of the user's picture, shown instead of the initials
  userPicture?: string | null;
  scale: number;
  x: number;
  y: number;
  viewerActions?: HighlightCreatorViewerActions;
  commentId: string;
  documentHighlight?: string | null;
  documentHighlightRects?: DocumentHighlightRectDto[];
  overObject?: { current: boolean };
  callback?: any;
};

type ReadyPinCreatorProps = {
  canvas: fabric.Canvas;
  obj: fabric.Object;
  canModify: boolean;
  userEmail: string;
  userName: string;
  // URL of the user's picture, shown instead of the initials
  userPicture?: string | null;
  overObject: { current: boolean };
  callback: any;
  scale: number;
};

const circleOptions = {
  radius: 11
};

const createPinLabelText = (label: string, left: number, top: number) =>
  new fabric.Text(label, {
    fontSize: 10,
    fontWeight: 400,
    left,
    top,
    stroke: 'black',
    fontFamily: 'RFDewi',
    originX: 'center',
    originY: 'center'
  });

/**
 * Keeps pins visible on white content. Only shown: saved annotations keep
 * no shadow, as before.
 */
const addPinShadow = (...objects: fabric.Object[]) => {
  objects.forEach((object) => {
    object.set(
      'shadow',
      new fabric.Shadow({
        color: 'rgba(0, 0, 0, 0.35)',
        blur: 3,
        offsetX: 0,
        offsetY: 0.5
      })
    );
    const toObject = object.toObject.bind(object);
    object.toObject = (propertiesToInclude?: string[]) => ({
      ...toObject(propertiesToInclude),
      shadow: null
    });
  });
};

const pinPictures = new Map<string, Promise<HTMLImageElement | null>>();

// Loaded with CORS: a picture without it would taint the canvas, and fabric
// finds pins by reading its pixels (perPixelTargetFind), which then throws.
// Such pictures fail to load here, and the initials are kept.
const loadPinPicture = (url: string) => {
  let picture = pinPictures.get(url);
  if (!picture) {
    picture = new Promise((resolve) => {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.onload = () => resolve(image);
      image.onerror = () => resolve(null);
      image.src = url;
    });
    pinPictures.set(url, picture);
  }
  return picture;
};

/**
 * The user's picture over the initials, like their avatar elsewhere. Empty
 * until loaded, and left out of the saved annotation.
 */
const createPinPicture = (
  left: number,
  top: number,
  pictureUrl: string | null | undefined
) => {
  const picture = new fabric.Circle({
    ...circleOptions,
    left,
    top,
    fill: '',
    strokeWidth: 1,
    excludeFromExport: true
  } as any);
  if (!pictureUrl) return picture;

  loadPinPicture(pictureUrl).then((image) => {
    if (!image) return;
    // Covers the circle, centered
    const diameter = circleOptions.radius * 2;
    const scale = diameter / Math.min(image.naturalWidth, image.naturalHeight);
    picture.set({
      fill: new fabric.Pattern({
        source: image,
        repeat: 'no-repeat',
        patternTransform: [
          scale,
          0,
          0,
          scale,
          (diameter - image.naturalWidth * scale) / 2,
          (diameter - image.naturalHeight * scale) / 2
        ]
      } as any),
      stroke: 'white'
    });
    // The canvas may have been disposed while the picture loaded (a PDF page
    // scrolled out of view): its objects keep it, but it has no context left
    const canvas = picture.group?.canvas as
      | (fabric.Canvas & { contextContainer?: CanvasRenderingContext2D | null })
      | undefined;
    if (canvas?.contextContainer) canvas.requestRenderAll();
  });
  return picture;
};

const pinOptions = {
  type: 'pin',
  scaleX: 1,
  scaleY: 1,
  originX: 'center',
  originY: 'bottom',
  objectCaching: false
};

export function pinCreate({
  e,
  x,
  y,
  canvas,
  userEmail,
  userName,
  userPicture,
  overObject,
  objectToCreate,
  scale
}: PinCreateProps) {
  const pointer = e ? canvas.getPointer(e.e) : undefined;
  const color = hashedColor(userEmail, 'profileAvatar');
  // Middle of the canvas, in coordinates
  const center = fabric.util.transformPoint(
    new fabric.Point(canvas.getWidth() / 2, canvas.getHeight() / 2),
    fabric.util.invertTransform(canvas.viewportTransform ?? [1, 0, 0, 1, 0, 0])
  );

  const origX = x ?? (pointer ? pointer.x : center.x);
  const origY = y ?? (pointer ? pointer.y : center.y);

  const text = createPinLabelText(userName, origX + 11.5, origY + 12);

  const circle = new fabric.Circle({
    ...circleOptions,
    left: origX,
    top: origY,
    fill: color,
    strokeWidth: 1,
    stroke: 'white'
  } as any);

  const triangle = new fabric.Triangle({
    width: 10,
    height: 6,
    angle: 180,
    fill: 'white',
    left: origX + 17,
    top: origY + 28.5
  });
  addPinShadow(circle, triangle);
  const picture = createPinPicture(origX, origY, userPicture);
  objectToCreate.current = new fabric.Group([circle, triangle, text, picture], {
    ...pinOptions,
    left: origX,
    top: origY,
    scaleX: scale,
    scaleY: scale,
    borderColor: 'transparent'
  });
  objectToCreate.current.setControlsVisibility({
    bl: false,
    br: false,
    mb: false,
    ml: false,
    mr: false,
    mt: false,
    tl: false,
    tr: false,
    mtr: false
  });
  objectToCreate.current.on('mouseup', () => {
    overObject.current = false;
  });
  objectToCreate.current.on('mouseover', () => {
    overObject.current = true;
  });
  objectToCreate.current.on('mousedown:before', () => {
    overObject.current = true;
  });
  objectToCreate.current.on('mouseout', () => {
    overObject.current = false;
  });
  canvas.add(objectToCreate.current);
  canvas.fire('object:pin:added');
}

export const pinPointCreator = ({
  canvas,
  userEmail,
  userName,
  userPicture,
  scale,
  x,
  y,
  viewerActions,
  commentId,
  callback,
  documentHighlight,
  documentHighlightRects,
  overObject
}: PinPointCreatorProps) => {
  const color = hashedColor(userEmail, 'profileAvatar');

  const text = createPinLabelText(userName, x + 11.5, y + 12);

  const circle = new fabric.Circle({
    ...circleOptions,
    left: x,
    top: y,
    fill: color,
    strokeWidth: 1,
    stroke: 'white'
  });
  addPinShadow(circle);
  const picture = createPinPicture(x, y, userPicture);
  const group = new fabric.Group([circle, text, picture], {
    ...pinOptions,
    left: x,
    top: y,
    scaleX: scale,
    scaleY: scale,
    borderColor: 'transparent'
  });
  group.setControlsVisibility({
    bl: false,
    br: false,
    mb: false,
    ml: false,
    mr: false,
    mt: false,
    tl: false,
    tr: false,
    mtr: false
  });

  group.selectable = false;
  group.hoverCursor = 'pointer';

  bindOverObjectRef(group, overObject);

  group.on('mouseup', () => {
    if (overObject) overObject.current = false;
    if (!viewerActions) return;
    markCommentTargetClick();
    viewerActions.selectComment(commentId);
    viewerActions.setCanvasMode('view');
    viewerActions.setDocumentHighlight(
      documentHighlight && documentHighlightRects?.length
        ? {
            text: documentHighlight,
            rects: documentHighlightRects
          }
        : null
    );
  });

  canvas.add(group);

  if (callback) callback();
};

export const readyPinCreator = ({
  canvas,
  obj,
  userEmail,
  userName,
  userPicture,
  canModify,
  overObject,
  callback,
  scale
}: ReadyPinCreatorProps) => {
  const color = hashedColor(userEmail, 'profileAvatar');

  const _obj = obj as any;
  const text = createPinLabelText(userName, _obj.left + 11.5, _obj.top + 12);

  const circle = new fabric.Circle({
    ...circleOptions,
    left: _obj.left,
    top: _obj.top,
    fill: color,
    strokeWidth: 1,
    stroke: 'white'
  } as any);

  const triangle = new fabric.Triangle({
    width: 10,
    height: 6,
    angle: 180,
    fill: 'white',
    left: _obj.left + 17,
    top: _obj.top + 28.5
  });
  addPinShadow(circle, triangle);
  const picture = createPinPicture(_obj.left, _obj.top, userPicture);
  const group = new fabric.Group([circle, triangle, text, picture], {
    ...pinOptions,
    left: _obj.left,
    top: _obj.top,
    scaleX: scale,
    scaleY: scale,
    borderColor: 'transparent'
  });
  group.setControlsVisibility({
    bl: false,
    br: false,
    mb: false,
    ml: false,
    mr: false,
    mt: false,
    tl: false,
    tr: false,
    mtr: false
  });

  if (!canModify) {
    group.selectable = false;
    group.hoverCursor = 'default';
  }

  group.on('mouseup', () => {
    overObject.current = false;
  });
  bindOverObjectRef(group, overObject);

  canvas.add(group);
  if (callback) callback();
};
